import React from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import BarChartIcon from '@mui/icons-material/BarChart';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import PublicIcon from '@mui/icons-material/Public';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import api from '../api';

const drawerWidth = 320;

import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';
const getMenuItems = () => {
    const items = [
        { name: 'Home', path: '/', icon: <HomeIcon /> },
        { name: 'Application Status', path: '/school-status', icon: <DisplaySettingsIcon /> },
        { name: 'User Management', path: '/users', icon: <GroupIcon /> },
        { name: 'Athletic Conferences', path: '/athletic-conferences', icon: <SportsBasketballIcon /> },
        { name: 'States', path: '/states', icon: <PublicIcon /> },
        { name: 'Schools', path: '/schools', icon: <SchoolIcon /> },
        { name: 'Templates', path: '/templates', icon: <IntegrationInstructionsIcon /> },
        { name: 'School Information', path: '/school-contacts', icon: <SchoolIcon /> },
    ];
    // Add Admin User Management for SUPER ADMIN only
    const role = localStorage.getItem('adminRole');
    if (role && role.toUpperCase() === 'SUPER ADMIN') {
        items.push({ name: 'Admin User Management', path: '/admin-users', icon: <GroupIcon /> });
    }
    return items;
};

const MainLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [openHistorical, setOpenHistorical] = React.useState(true);
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [whoami, setWhoami] = React.useState(null);
    const [accountAnchorEl, setAccountAnchorEl] = React.useState(null);

    React.useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await api.get('/admin/api/schools/whoami');
                if (!mounted) return;
                setWhoami(res.data || null);
            } catch (e) {
                if (mounted) setWhoami(null);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('adminRole');
        window.location.href = '/';
    };

    const handleOpenAccountMenu = (event) => {
        setAccountAnchorEl(event.currentTarget);
    };

    const handleCloseAccountMenu = () => {
        setAccountAnchorEl(null);
    };

    const handleOpenAccountPage = () => {
        handleCloseAccountMenu();
        navigate('/account');
    };

    const handleLogoutFromMenu = () => {
        handleCloseAccountMenu();
        handleLogout();
    };

    const drawer = (
        <div>
            <Toolbar>
                <Typography variant="h6" noWrap component="div" sx={{ width: '100%', textAlign: 'center' }}>
                    RYS Admin
                </Typography>
            </Toolbar>
            <Divider />
            <List>
                {getMenuItems().map((item) => (
                    <ListItem key={item.name} disablePadding>
                        <ListItemButton
                            component={Link}
                            to={item.path}
                            selected={location.pathname === item.path}
                            sx={{ pl: 3 }}
                        >
                            <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.name} />
                        </ListItemButton>
                    </ListItem>
                ))}

                <ListItem disablePadding>
                    <ListItemButton onClick={() => setOpenHistorical(!openHistorical)} sx={{ pl: 3 }}>
                        <ListItemIcon sx={{ color: 'inherit' }}>
                            <QueryStatsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Historical Data" />
                        {openHistorical ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>
                <Collapse in={openHistorical} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <ListItem disablePadding>
                            <ListItemButton component={Link} to={'/historical-winners'} selected={location.pathname === '/historical-winners'} sx={{ pl: 6 }}>
                                <ListItemIcon sx={{ color: 'inherit' }}><EmojiEventsIcon /></ListItemIcon>
                                <ListItemText primary="Grant Recipients" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton component={Link} to={'/yoy-participation'} selected={location.pathname === '/yoy-participation'} sx={{ pl: 6 }}>
                                <ListItemIcon sx={{ color: 'inherit' }}><BarChartIcon /></ListItemIcon>
                                <ListItemText primary="YOY Participation" />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Collapse>
            </List>
        </div>
    );
    const hideShell = location && location.pathname && location.pathname.startsWith('/accept-invite');

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {!hideShell && (
                <AppBar
                    position="fixed"
                    sx={{
                        width: { sm: `calc(100% - ${drawerWidth}px)` },
                        ml: { sm: `${drawerWidth}px` },
                        background: '#222',
                    }}
                    elevation={1}
                >
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2, display: { sm: 'none' } }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Box sx={{ flexGrow: 1 }} />
                        {whoami && (
                            <Typography variant="body2" sx={{ color: '#fff', mr: 2 }}>
                                {whoami.principal} — {Array.isArray(whoami.roles) ? whoami.roles.join(', ') : String(whoami.roles)}
                            </Typography>
                        )}
                        <IconButton
                            color="inherit"
                            aria-label="account menu"
                            onClick={handleOpenAccountMenu}
                            sx={{ ml: 1 }}
                        >
                            <AccountCircleIcon />
                        </IconButton>
                        <Menu
                            anchorEl={accountAnchorEl}
                            open={Boolean(accountAnchorEl)}
                            onClose={handleCloseAccountMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem onClick={handleOpenAccountPage}>Update Account</MenuItem>
                            <MenuItem onClick={handleLogoutFromMenu}>Logout</MenuItem>
                        </Menu>
                    </Toolbar>
                </AppBar>
            )}
            {!hideShell && (
                <Box
                    component="nav"
                    sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                    aria-label="sidebar menus"
                >
                    <>
                        {/* Mobile drawer */}
                        <Drawer
                            variant="temporary"
                            open={mobileOpen}
                            onClose={handleDrawerToggle}
                            ModalProps={{ keepMounted: true }}
                            sx={{
                                display: { xs: 'block', sm: 'none' },
                                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                            }}
                        >
                            {drawer}
                        </Drawer>
                        {/* Desktop drawer */}
                        <Drawer
                            variant="permanent"
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                            }}
                            open
                        >
                            {drawer}
                        </Drawer>
                    </>
                </Box>
            )}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: hideShell ? '100%' : { sm: `calc(100% - ${drawerWidth}px)` },
                    background: '#fafbfc',
                    minHeight: '100vh',
                }}
            >
                {!hideShell && <Toolbar />}
                {children}
            </Box>
        </Box>
    );
};

export default MainLayout;
